import { useCallback, useMemo } from 'react';

import { useUpdateSitrepV2 } from 'components/DisasterGPT/hooks/useSitrepsV2';
import {
  isReportsLayoutMode,
  useReportsLayoutMode,
  type ReportsLayoutMode,
} from 'components/Reports/hooks/useReportsLayoutMode';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';

/**
 * The layout a persisted `layout_mode` should open in. Reports saved while the
 * SitStat layout existed still carry `'sitstat'`; they open as Document, which
 * is the closest surviving layout. Anything unknown defers to the browser
 * default (`null`).
 */
export function resolvePersistedSitrepLayoutMode(
  persisted: unknown
): ReportsLayoutMode | null {
  if (isReportsLayoutMode(persisted)) return persisted;
  if (persisted === 'sitstat') return 'document';
  return null;
}

export function useSitrepLayoutMode(
  sitrep: GetSitrepsResponse,
  detail?: Pick<GetSitrepsResponse, 'layout_mode'> | null
) {
  const { layoutMode: storedMode, setLayoutMode: setStoredMode } =
    useReportsLayoutMode();
  const updateSitrep = useUpdateSitrepV2();
  const persistedMode = detail?.layout_mode ?? sitrep.layout_mode;

  const layoutMode: ReportsLayoutMode = useMemo(
    () => resolvePersistedSitrepLayoutMode(persistedMode) ?? storedMode,
    [persistedMode, storedMode]
  );

  const setLayoutMode = useCallback(
    (mode: ReportsLayoutMode) => {
      setStoredMode(mode);
      updateSitrep.mutate({
        sitrep_id: sitrep.id,
        layout_mode: mode,
      });
    },
    [setStoredMode, sitrep.id, updateSitrep]
  );

  return { layoutMode, setLayoutMode };
}
